import { styled } from '@linaria/react';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

interface AgentPromptRejectionProps {
  /** Why the backend refused the last answer or approval. */
  message: string;
}

/**
 * Inline notice on a question or approval card whose last submit the backend
 * refused. The card stays answerable underneath, so the notice only explains
 * why the previous attempt didn't go through. Announced to assistive
 * technology as an alert.
 *
 * @param {AgentPromptRejectionProps} props - The component props
 * @param {string} props.message - The refusal reason to show
 * @returns {JSX.Element} The rejection notice
 *
 * @example
 * <AgentPromptRejection message="Failed to submit answers. Please try again." />
 */
export function AgentPromptRejection({ message }: AgentPromptRejectionProps) {
  return (
    <Notice role="alert">
      <Icon name="error_outline" size={16} color="var(--red-base)" />
      <Text size="small" color="var(--ink-dark)">
        {message}
      </Text>
    </Notice>
  );
}

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 12px;
  padding: 8px 10px;
  border: 1px solid var(--red-lighter);
  border-radius: 8px;
  background: var(--red-lightest);
`;
